<template>
  <div class="order-proposal">
    <!-- En-tête -->
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div>
        <h2 class="mb-0 font-weight-bolder">
          Proposition de commande
        </h2>
        <small class="text-muted">
          {{ period ? `Vitesse de vente : ${period}` : '' }}
          <span v-if="lastUpdate"> · Calculé le {{ lastUpdate }}</span>
        </small>
      </div>
      <div class="d-flex">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mr-1"
          :disabled="!filteredProposals.length"
          @click="exportCsv"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          Export CSV
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="loading"
          @click="fetchProposals"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Recalculer
        </b-button>
      </div>
    </div>

    <div
      v-if="loading && !proposals.length"
      class="d-flex flex-column align-items-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <small class="text-muted mt-1">Calcul en cours (ventes sur 52 semaines)…</small>
    </div>

    <template v-else>
      <!-- Cartes de synthèse -->
      <b-row class="mb-1">
        <b-col
          md="3"
          sm="6"
        >
          <b-card class="text-center">
            <h3 class="font-weight-bolder mb-0">
              {{ filteredProposals.length }}
            </h3>
            <small class="text-muted">SKUs à commander</small>
          </b-card>
        </b-col>
        <b-col
          md="2"
          sm="6"
        >
          <b-card class="text-center">
            <h3 class="font-weight-bolder mb-0 text-danger">
              {{ countByUrgency('URGENT') }}
            </h3>
            <small class="text-muted">Urgents</small>
          </b-card>
        </b-col>
        <b-col
          md="2"
          sm="6"
        >
          <b-card class="text-center">
            <h3 class="font-weight-bolder mb-0 text-warning">
              {{ countByUrgency('À COMMANDER') }}
            </h3>
            <small class="text-muted">À commander</small>
          </b-card>
        </b-col>
        <b-col
          md="2"
          sm="6"
        >
          <b-card class="text-center">
            <h3 class="font-weight-bolder mb-0 text-secondary">
              {{ countByUrgency('À ANTICIPER') }}
            </h3>
            <small class="text-muted">À anticiper</small>
          </b-card>
        </b-col>
        <b-col
          md="3"
          sm="6"
        >
          <b-card class="text-center">
            <h3 class="font-weight-bolder mb-0">
              {{ sumOrderQty(filteredProposals) }}
            </h3>
            <small class="text-muted">Unités à commander</small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Filtres -->
      <b-card class="mb-2">
        <b-row>
          <b-col
            md="4"
            class="mb-1 mb-md-0"
          >
            <b-form-input
              v-model="search"
              placeholder="Rechercher un SKU…"
            />
          </b-col>
          <b-col
            md="3"
            class="mb-1 mb-md-0"
          >
            <b-form-select
              v-model="supplierFilter"
              :options="supplierOptions"
            />
          </b-col>
          <b-col
            md="3"
            class="mb-1 mb-md-0"
          >
            <b-form-select
              v-model="urgencyFilter"
              :options="urgencyOptions"
            />
          </b-col>
          <b-col
            md="2"
            class="d-flex align-items-center"
          >
            <b-link
              v-if="search || supplierFilter || urgencyFilter"
              @click="resetFilters"
            >
              Réinitialiser
            </b-link>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        v-if="!filteredProposals.length"
        class="text-center py-4"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="48"
          class="text-success mb-1"
        />
        <p class="mb-0 text-muted">
          Aucune proposition ne correspond aux filtres.
        </p>
      </b-card>

      <!-- Tableau par fabricant -->
      <b-card
        v-for="(items, supplier) in groupedBySupplier"
        :key="supplier"
        no-body
        class="mb-2"
      >
        <b-card-header class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0 font-weight-bolder">
            <feather-icon
              icon="TruckIcon"
              size="18"
              class="mr-50"
            />
            {{ supplier }}
          </h4>
          <small class="text-muted">
            {{ items.length }} SKU(s) · {{ sumOrderQty(items) }} unités
          </small>
        </b-card-header>

        <b-table
          :items="items"
          :fields="tableColumns"
          responsive
          hover
          small
          primary-key="skuPremier"
          @row-clicked="toggleDetails"
        >
          <template #cell(toggle)="data">
            <feather-icon
              :icon="data.detailsShowing ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="16"
              class="cursor-pointer text-muted"
            />
          </template>

          <template #cell(urgency)="data">
            <b-badge
              pill
              :variant="resolveUrgencyVariant(data.item.urgency)"
            >
              {{ resolveUrgencyLabel(data.item.urgency) }}
            </b-badge>
          </template>

          <template #cell(skuPremier)="data">
            <span class="font-weight-bold">{{ data.item.skuPremier }}</span>
            <div v-if="data.item.details.length > 1">
              <small class="text-muted">{{ data.item.details.map(d => d.sku).join(', ') }}</small>
            </div>
          </template>

          <template #cell(category)="data">
            <small>{{ data.item.category }}</small>
          </template>

          <template #cell(weeklyVelocity)="data">
            {{ data.item.weeklyVelocity }}
            <div>
              <small class="text-muted">{{ data.item.velocityMethod }}</small>
            </div>
          </template>

          <template #cell(coverage)="data">
            {{ formatCoverage(data.item) }}
            <div>
              <small class="text-muted">seuil {{ data.item.orderPointMonths }} mois</small>
            </div>
          </template>

          <template #cell(orderQty)="data">
            <span class="font-weight-bolder">{{ data.item.orderQty }}</span>
          </template>

          <!-- Détail par version -->
          <template #row-details="data">
            <b-table-lite
              :items="data.item.details"
              :fields="detailColumns"
              small
              class="mb-0 bg-light"
            >
              <template #cell(firstSale)="d">
                {{ formatDate(d.item.firstSale) }}
              </template>
            </b-table-lite>
          </template>
        </b-table>
      </b-card>

      <!-- Groupes ignorés -->
      <b-card
        v-if="ignored.length"
        class="mb-0"
      >
        <b-link @click="showIgnored = !showIgnored">
          <feather-icon
            :icon="showIgnored ? 'ChevronDownIcon' : 'ChevronRightIcon'"
            size="14"
          />
          {{ ignored.length }} groupe(s) ignoré(s)
        </b-link>
        <ul
          v-if="showIgnored"
          class="mt-1 mb-0 pl-2"
        >
          <li
            v-for="(i, idx) in ignored"
            :key="idx"
          >
            <small class="text-muted">{{ i }}</small>
          </li>
        </ul>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BTable, BTableLite, BBadge, BSpinner, BLink, BButton,
  BRow, BCol, BFormInput, BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'OrderProposal',

  components: {
    BCard,
    BCardHeader,
    BTable,
    BTableLite,
    BBadge,
    BSpinner,
    BLink,
    BButton,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
  },

  data() {
    return {
      loading: false,
      proposals: [],
      ignored: [],
      period: '',
      lastUpdate: '',
      search: '',
      supplierFilter: null,
      urgencyFilter: null,
      showIgnored: false,
      urgencyOptions: [
        { value: null, text: 'Toutes les urgences' },
        { value: 'URGENT', text: 'Urgent' },
        { value: 'À COMMANDER', text: 'À commander' },
        { value: 'À ANTICIPER', text: 'À anticiper' },
      ],
      tableColumns: [
        { key: 'toggle', label: '' },
        { key: 'urgency', label: 'Urgence', sortable: true },
        { key: 'skuPremier', label: 'SKU premier', sortable: true },
        { key: 'category', label: 'Catégorie', sortable: true },
        {
          key: 'currentStock', label: 'Stock', sortable: true, class: 'text-right',
        },
        {
          key: 'pendingBl', label: 'BL', sortable: true, class: 'text-right',
        },
        {
          key: 'weeklyVelocity', label: 'Ventes/sem', sortable: true, class: 'text-right',
        },
        { key: 'coverage', label: 'Couverture', class: 'text-right' },
        {
          key: 'targetQty', label: 'Besoin', sortable: true, class: 'text-right',
        },
        { key: 'moq', label: 'MOQ', class: 'text-right' },
        {
          key: 'orderQty', label: 'À commander', sortable: true, class: 'text-right',
        },
      ],
      detailColumns: [
        { key: 'sku', label: 'Version' },
        { key: 'stock', label: 'Stock', class: 'text-right' },
        { key: 'pendingBl', label: 'BL', class: 'text-right' },
        { key: 'sales12', label: 'Ventes 12 sem', class: 'text-right' },
        { key: 'sales52', label: 'Ventes 52 sem', class: 'text-right' },
        { key: 'firstSale', label: '1ère vente', class: 'text-right' },
      ],
    }
  },

  computed: {
    supplierOptions() {
      const suppliers = [...new Set(this.proposals.map(p => p.supplier))].sort()
      return [
        { value: null, text: 'Tous les fabricants' },
        ...suppliers.map(s => ({ value: s, text: s })),
      ]
    },

    filteredProposals() {
      const q = this.search.trim().toLowerCase()
      return this.proposals.filter(p => {
        if (this.supplierFilter && p.supplier !== this.supplierFilter) return false
        if (this.urgencyFilter && !p.urgency.includes(this.urgencyFilter)) return false
        if (q) {
          const skus = [p.skuPremier, ...p.details.map(d => d.sku)].join(' ').toLowerCase()
          if (!skus.includes(q)) return false
        }
        return true
      })
    },

    groupedBySupplier() {
      return this.filteredProposals.reduce((acc, p) => {
        if (!acc[p.supplier]) acc[p.supplier] = []
        acc[p.supplier].push(p)
        return acc
      }, {})
    },
  },

  mounted() {
    this.fetchProposals()
  },

  methods: {
    async fetchProposals() {
      try {
        this.loading = true
        // ⚠️ adapte à la route qui expose generateOrderProposal
        const { proposals, ignored, period } = await this.$http.get('/admin/logistic/order-proposal')
        this.proposals = (proposals || []).map(p => ({ ...p, _showDetails: false }))
        this.ignored = ignored || []
        this.period = period || ''
        this.lastUpdate = new Date().toLocaleString('fr-FR')
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Impossible de calculer la proposition de commande.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    toggleDetails(item) {
      this.$set(item, '_showDetails', !item._showDetails)
    },

    resetFilters() {
      this.search = ''
      this.supplierFilter = null
      this.urgencyFilter = null
    },

    countByUrgency(label) {
      return this.filteredProposals.filter(p => p.urgency.includes(label)).length
    },

    sumOrderQty(items) {
      return items.reduce((acc, p) => acc + (p.orderQty || 0), 0)
    },

    resolveUrgencyVariant(urgency) {
      if (urgency.includes('URGENT')) return 'light-danger'
      if (urgency.includes('À COMMANDER')) return 'light-warning'
      return 'light-secondary'
    },

    resolveUrgencyLabel(urgency) {
      return urgency.replace(/^\S+\s/, '')
    },

    formatCoverage(item) {
      if (item.currentCoverage >= 999) return '∞'
      return `${item.coverageMonths} mois`
    },

    formatDate(date) {
      if (!date) return 'n/a'
      return new Date(date).toLocaleDateString('fr-FR')
    },

    toCsvCell(value) {
      const str = value === null || value === undefined ? '' : String(value)
      return `"${str.replace(/"/g, '""')}"`
    },

    exportCsv() {
      const header = [
        'Fabricant', 'Urgence', 'SKU premier', 'Versions', 'Catégorie',
        'Stock', 'BL', 'Ventes/sem', 'Base', 'Couverture (mois)',
        'Besoin', 'MOQ', 'À commander',
      ]
      const rows = this.filteredProposals.map(p => [
        p.supplier,
        this.resolveUrgencyLabel(p.urgency),
        p.skuPremier,
        p.details.map(d => d.sku).join(' | '),
        p.category,
        p.currentStock,
        p.pendingBl,
        String(p.weeklyVelocity).replace('.', ','),
        p.velocityMethod,
        p.currentCoverage >= 999 ? '' : String(p.coverageMonths).replace('.', ','),
        p.targetQty,
        p.moq,
        p.orderQty,
      ])

      const csv = [header, ...rows]
        .map(r => r.map(v => this.toCsvCell(v)).join(';'))
        .join('\n')

      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `proposition-commande-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>
